import React, { useCallback } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportTable from "components/reports/shared/ReportTable";
import { formatPercent as formatPercentValue } from "components/reports/shared/reportFormatters";
import { getChartColors } from "theme";
import api from "services/api";

const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Discount & Margin Leakage
 *
 * Every discount is billed work given away after the fact, so the report keeps
 * it separate from cost of revenue and names the clients, invoices and
 * approvals responsible.
 */
export const DiscountLeakage = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/invoice/discounts", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const hasDiscounts = (summary.discountedInvoices || 0) > 0;

      return [
        {
          title: "Total Discount Given",
          value: formatCurrency(summary.totalDiscount),
          subtitle: `${summary.discountedInvoices || 0} of ${
            summary.invoicesInPeriod || 0
          } invoices`,
          color: hasDiscounts ? "warning.main" : "success.main",
          tooltipTitle: "Discount Leakage",
          tooltipContent:
            "Sum of every invoice discount in the selected period. This is revenue that was billed at list price and then reduced, so it is separate from (and additive to) margin lost through cost of delivery.",
        },
        {
          title: "Discount Rate",
          value: formatPercentValue(summary.discountRate, { digits: 2 }),
          subtitle: "of gross billings",
          color: "text.secondary",
          tooltipTitle: "Discount Rate",
          tooltipContent:
            "Total discounts divided by gross billings (billed amount + discount). Below 1% is usually a rounding/adjustment pattern; higher values point at pricing pressure or discretionary concessions.",
        },
        {
          title: "Largest Single Discount",
          value: formatCurrency(summary.largestDiscount),
          subtitle: "single invoice",
        },
        {
          title: "Unapproved Discounts",
          value: formatCurrency(summary.unapprovedAmount),
          subtitle: `${summary.unapprovedCount || 0} without approval`,
          color: (summary.unapprovedCount || 0) > 0 ? "error.main" : "success.main",
          tooltipTitle: "Approval Gap",
          tooltipContent:
            "Discounts with no approver recorded. These are the ones to review first: they carry the same revenue impact as approved discounts but bypassed the sign-off.",
        },
      ];
    },
    [],
  );

  const chartsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const monthlyTrend = data.monthlyTrend || [];
      const clientRows = data.clientRows || [];
      const invoiceRows = data.invoiceRows || [];
      const typeBreakdown = data.typeBreakdown || [];

      if ((summary.discountedInvoices || 0) === 0) {
        return (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              bgcolor: theme.palette.background.alt,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.primary.border2}`,
            }}
          >
            <Typography variant="body1" fontWeight={600} gutterBottom>
              No invoice discounts in this period
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every invoice was billed at list price. Pick a wider period to look
              for historical leakage.
            </Typography>
          </Box>
        );
      }

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: monthlyTrend.length > 1 ? 7 : 12 }}>
            {monthlyTrend.length > 1 ? (
              <ReportLineChart
                title="Discount Trend"
                xAxisData={monthlyTrend.map((row) => row.month)}
                series={[
                  {
                    data: monthlyTrend.map((row) => row.discount),
                    label: "Discount given",
                    color: chartColors.semantic.warning,
                  },
                  {
                    data: monthlyTrend.map((row) => row.billed),
                    label: "Billed",
                    color: chartColors.semantic.primary,
                  },
                ]}
                valueFormatter={formatCurrency}
                yAxisLabel="Amount"
                height={360}
                tooltipTitle="Discount Trend"
                tooltipContent="Discounts per month against the billing it was taken from. A rising discount line on flat billings is margin leaving the business."
              />
            ) : (
              <ReportTable
                title="Discount by Month"
                columns={[
                  { field: "month", headerName: "Month" },
                  {
                    field: "billed",
                    headerName: "Billed",
                    align: "right",
                    renderCell: (row) => formatCurrency(row.billed),
                  },
                  {
                    field: "discount",
                    headerName: "Discount",
                    align: "right",
                    renderCell: (row) => formatCurrency(row.discount),
                  },
                  {
                    field: "discountRate",
                    headerName: "Rate",
                    align: "right",
                    renderCell: (row) =>
                      formatPercentValue(row.discountRate, { digits: 2 }),
                  },
                ]}
                rows={monthlyTrend}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12, lg: monthlyTrend.length > 1 ? 5 : 12 }}>
            <ReportPieChart
              title="Discount Type"
              data={typeBreakdown.map((row) => ({
                label: row.label,
                value: row.amount,
              }))}
              valueFormatter={formatCurrency}
              height={360}
              innerRadius={50}
              outerRadius={95}
              tooltipTitle="Discount Type"
              tooltipContent="How discounts were expressed (fixed amount or percentage). Percentage discounts scale with the invoice, so a single percentage rule can leak far more than its face value suggests."
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ReportTable
              title="Discounts by Client"
              subtitle="Ranked by discount amount. Rate is measured against that client's gross billings."
              columns={[
                { field: "clientName", headerName: "Client", width: 260 },
                { field: "invoiceCount", headerName: "Invoices", align: "right" },
                {
                  field: "billed",
                  headerName: "Billed",
                  align: "right",
                  renderCell: (row) => formatCurrency(row.billed),
                },
                {
                  field: "discount",
                  headerName: "Discount",
                  align: "right",
                  renderCell: (row) => formatCurrency(row.discount),
                },
                {
                  field: "discountRate",
                  headerName: "Rate",
                  align: "right",
                  renderCell: (row) =>
                    formatPercentValue(row.discountRate, { digits: 2 }),
                },
              ]}
              rows={clientRows}
              emptyMessage="No discounted invoices in this period"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ReportTable
              title="Discounted Invoices"
              subtitle="Every invoice with a discount, largest first."
              columns={[
                { field: "invoiceNumber", headerName: "Invoice #", width: 110 },
                { field: "clientName", headerName: "Client", width: 220 },
                {
                  field: "servicesDate",
                  headerName: "Service Date",
                  renderCell: (row) => formatDate(row.servicesDate || row.invoiceDate),
                },
                {
                  field: "billedAmount",
                  headerName: "Billed",
                  align: "right",
                  renderCell: (row) => formatCurrency(row.billedAmount),
                },
                {
                  field: "discountAmount",
                  headerName: "Discount",
                  align: "right",
                  renderCell: (row) => formatCurrency(row.discountAmount),
                },
                {
                  field: "discountType",
                  headerName: "Type",
                  renderCell: (row) => row.discountType || "-",
                },
                {
                  field: "approved",
                  headerName: "Approved",
                  renderCell: (row) =>
                    row.approved ? "Yes" : "No",
                },
                {
                  field: "discountNote",
                  headerName: "Note",
                  renderCell: (row) => row.discountNote || "-",
                },
              ]}
              rows={invoiceRows}
              emptyMessage="No discounted invoices in this period"
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors, theme],
  );

  return (
    <DashboardReport
      title="Discount & Margin Leakage"
      description="Invoice-level discounts measured against gross billings, with the clients, approvals and individual invoices responsible."
      dataFetchFn={dataFetchFn}
      filters={filters}
      metricsConfig={metricsConfig}
      chartsConfig={chartsConfig}
    />
  );
};

/**
 * Cash Flow Forecast
 *
 * Forward-looking by design: always reports the open book as of today, so the
 * month/year filter is intentionally ignored here.
 */
export const CashFlowForecast = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const dataFetchFn = useCallback(async () => {
    const response = await api.get("/reports/invoice/cashflow-forecast");
    return response.data;
  }, []);

  const metricsConfig = useCallback((data) => {
    const summary = data.summary || {};

    return [
      {
        title: "Open Receivables",
        value: formatCurrency(summary.openAmount),
        subtitle: `${summary.openInvoices || 0} unpaid invoices`,
        color: "primary.main",
      },
      {
        title: "Overdue",
        value: formatCurrency(summary.overdueAmount),
        subtitle: `${summary.overdueInvoices || 0} invoices past due`,
        color: "error.main",
        tooltipTitle: "Overdue",
        tooltipContent:
          "Already due and still unpaid. This is the portion that needs collections action now, not forecasting.",
      },
      {
        title: "Due Within 7 Days",
        value: formatCurrency(summary.dueIn7),
        subtitle: "expected this week",
        color: "warning.main",
      },
      {
        title: "Expected in 30 Days",
        value: formatCurrency(summary.projected30),
        subtitle: "cumulative, excluding overdue",
        color: "success.main",
      },
      {
        title: "Expected in 60 Days",
        value: formatCurrency(summary.projected60),
        subtitle: "cumulative, excluding overdue",
      },
      {
        title: "Expected in 90 Days",
        value: formatCurrency(summary.projected90),
        subtitle: "cumulative, excluding overdue",
      },
    ];
  }, []);

  const chartsConfig = useCallback(
    (data) => {
      const buckets = data.buckets || [];
      const weekly = data.weekly || [];
      const clientRows = data.clientRows || [];

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <ReportBarChart
              title="Receivables by Due Window"
              data={buckets.map((row) => ({ ...row, label: row.label }))}
              xAxisKey="label"
              series={[
                {
                  dataKey: "amount",
                  label: "Amount due",
                  color: chartColors.semantic.primary,
                },
              ]}
              valueFormatter={formatCurrency}
              yAxisLabel="Amount"
              height={360}
              tooltipTitle="Receivables by Due Window"
              tooltipContent="Unpaid invoices grouped by when they are due. Overdue money is already late; the other windows show what should arrive if clients pay on time."
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <ReportLineChart
              title="Expected Receipts — Next 12 Weeks"
              xAxisData={weekly.map((row) => row.label)}
              series={[
                {
                  data: weekly.map((row) => row.amount),
                  label: "Expected",
                  color: chartColors.semantic.positive,
                },
              ]}
              valueFormatter={formatCurrency}
              yAxisLabel="Amount"
              height={360}
              tooltipTitle="Weekly Forecast"
              tooltipContent="Future-dated invoices placed in the week their due date falls. Overdue invoices are excluded here because they are reported separately."
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ReportTable
              title="Top Clients by Open Balance"
              subtitle={`As of ${formatDate(data.asOf)}. Next due date is the earliest open invoice for that client.`}
              columns={[
                { field: "clientName", headerName: "Client", width: 280 },
                { field: "invoiceCount", headerName: "Open Invoices", align: "right" },
                {
                  field: "amount",
                  headerName: "Open Amount",
                  align: "right",
                  renderCell: (row) => formatCurrency(row.amount),
                },
                {
                  field: "nextDueDate",
                  headerName: "Next Due",
                  renderCell: (row) => formatDate(row.nextDueDate),
                },
              ]}
              rows={clientRows}
              emptyMessage="No open invoices — every invoice is paid"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.primary.border2}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                This report always reflects the open book as of today, so the
                month/year filter does not apply. Use the Aging & Collections
                report for a period view of the same invoices.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      );
    },
    [chartColors, theme],
  );

  return (
    <DashboardReport
      title="Cash Flow Forecast"
      description="Open receivables bucketed by due date, with expected receipts over the next 12 weeks."
      dataFetchFn={dataFetchFn}
      filters={filters}
      metricsConfig={metricsConfig}
      chartsConfig={chartsConfig}
    />
  );
};
